<script setup lang="ts">
import { type AnimationPlaybackControlsWithThen, stagger, useAnimate, useInView } from 'motion-v'

const months = eachMonthOfInterval({
  start: startOfYear(new Date()),
  end: addMonths(startOfYear(new Date()), 6),
}).map(date => format(date, 'MMM'))

const [scope, animate] = useAnimate()
const isInView = useInView(scope, {
  amount: 'all',
})

let beforeSequence: AnimationPlaybackControlsWithThen | undefined
let afterSequence: AnimationPlaybackControlsWithThen | undefined

watch(isInView, async (inView) => {
  beforeSequence?.stop()
  afterSequence?.stop()

  beforeSequence = animate([
    [
      '[data-id=chart]:not(:first-child)',
      { opacity: 0, scale: 0 },
      { duration: 0 },
    ],
    [
      '[data-id=chart-graph]',
      { opacity: 0 },
      { duration: 0 },
    ],
    [
      '[data-id=chart-bar]',
      { opacity: 0, height: 0 },
      { duration: 0 },
    ],
    [
      '[data-id=chart-line-track]',
      { '--value': 4, '--previous-value': 4 },
      { duration: 0 },
    ],
    [
      '[data-id=chart-line-dot]',
      { bottom: 0 },
      { duration: 0 },
    ],
  ])

  await beforeSequence.finished

  if (!inView) return

  afterSequence = animate([
    [
      '[data-id=chart]:nth-child(1)',
      { opacity: 1, scale: 1 },
      { duration: 0.3 },
    ],
    [
      '[data-id=chart]:nth-child(1) [data-id=chart-graph]',
      { opacity: 1 },
      { duration: 0.3, at: '-0.3' },
    ],
    [
      '[data-id=chart]:nth-child(1) [data-id=chart-bar]',
      { opacity: 1, height: 'var(--tw-height)' },
      { delay: stagger(0.05) },
    ],
    [
      '[data-id=chart]:nth-child(2)',
      { opacity: 1, scale: 1 },
      { duration: 0.3 },
    ],
    [
      '[data-id=chart]:nth-child(2) [data-id=chart-graph]',
      { opacity: 1 },
      { duration: 0.3 },
    ],
    [
      '[data-id=chart]:nth-child(2) [data-id=chart-line-track]',
      { '--value': 'var(--value-final)', '--previous-value': 'var(--previous-value-final)' },
      { duration: 0.4 },
    ],
    [
      '[data-id=chart]:nth-child(2) [data-id=chart-line-dot]',
      { bottom: 'var(--tw-bottom)' },
      { duration: 0.4, at: '-0.4' },
    ],
    [
      '[data-id=chart]:nth-child(3)',
      { opacity: 1, scale: 1 },
      { duration: 0.3 },
    ],
    [
      '[data-id=chart]:nth-child(3) [data-id=chart-graph]',
      { opacity: 1 },
      { duration: 0.3 },
    ],
    [
      '[data-id=chart]:nth-child(3) [data-id=chart-bar]',
      { opacity: 1, height: 'var(--tw-height)' },
      { delay: stagger(0.05) },
    ],
  ])

  await afterSequence.finished
})

function getHeight(index: number) {
  // Formula: pairs of (10+20) = 30px per pair, plus remainder
  const pairs = Math.floor(index / 2)
  const remainder = index % 2

  // Start at 40px, alternate increments of +10 and +20
  return 40 + (pairs * 30) + (remainder * 10)
}
</script>

<template>
  <div
    ref="scope"
    class="grid grid-flow-row auto-rows-auto place-items-start -space-y-20"
  >
    <div
      data-id="chart"
      class="isolate grid grid-flow-row auto-rows-auto gap-y-2 rounded-2xl bg-white p-4 shadow ring-1 ring-neutral-200"
    >
      <span class="font-medium">
        Website Visits
      </span>

      <div
        class="grid auto-cols-fr grid-flow-col items-end gap-x-2"
        data-id="chart-graph"
        :style="{ opacity: 0 }"
      >
        <div
          v-for="(month, monthIndex) in months"
          :key="month"
          class="grid place-content-end gap-y-2 text-center"
          :style="{
            minHeight: `${getHeight(months.length - 1) + 24}px`
          }"
        >
          <span
            data-id="chart-bar"
            class="block w-full rounded-lg bg-emerald-500"
            :style="{
              '--tw-height': `${getHeight(monthIndex)}px`,
              '--tw-bg-opacity': (1 / 7 * (monthIndex + 1)).toFixed(2)
            }"
          />

          <span class="min-w-6 text-xs text-neutral-500">
            {{ month }}
          </span>
        </div>
      </div>
    </div>

    <div
      data-id="chart"
      class="isolate mx-auto grid grid-flow-row auto-rows-auto gap-y-2 rounded-2xl bg-white p-4 shadow ring-1 ring-neutral-200"
      :style="{ opacity: 0 }"
    >
      <span class="font-medium">
        Average Check Size
      </span>

      <div
        data-id="chart-graph"
        :style="{ opacity: 0 }"
      >
        <div class="relative h-[150px] overflow-hidden">
          <!-- Grid lines -->
          <div class="absolute inset-0 flex flex-col justify-between pt-4">
            <div
              v-for="i in 5"
              :key="i"
              class="h-px w-full bg-neutral-200"
            />
          </div>

          <!-- Line segments using clip-path -->
          <div class="absolute inset-0">
            <div
              v-for="(_, monthIndex) in months.slice(0, -1)"
              :key="`line-${monthIndex}`"
              data-id="chart-line-track"
              class="absolute h-full bg-sky-500"
              :style="{
                '--value-final': getHeight(monthIndex + 1),
                '--previous-value-final': getHeight(monthIndex),
                '--value': 0,
                '--previous-value': 0,
                left: `calc((${monthIndex} + 0.5) / ${months.length} * 100% + ${monthIndex} / ${months.length} * 0.5rem)`,
                width: `calc(100% / ${months.length} + 0.5rem / ${months.length})`,
                clipPath: `polygon(
                0 calc(100% - (var(--previous-value, 0) * 1px) - 1px),
                100% calc(100% - (var(--value, 0) * 1px) - 1px),
                100% calc(100% - (var(--value, 0) * 1px) - 1px + 2px),
                0 calc(100% - (var(--previous-value, 0) * 1px) - 1px + 2px)
              )`
              }"
            />
          </div>

          <!-- Dots -->
          <div class="absolute inset-0 grid auto-cols-fr grid-flow-col gap-x-2">
            <div
              v-for="(month, monthIndex) in months"
              :key="month"
              class="relative"
            >
              <div
                data-id="chart-line-dot"
                class="absolute left-1/2 size-2 -translate-x-1/2 rounded-full bg-sky-500"
                :style="{
                  '--tw-bottom': `${getHeight(monthIndex) - 5}px`
                }"
              />
            </div>
          </div>
        </div>

        <!-- Month labels -->
        <div class="mt-2 grid auto-cols-fr grid-flow-col gap-x-2 text-center">
          <span
            v-for="month in months"
            :key="month"
            class="min-w-6 text-xs text-neutral-500"
          >
            {{ month }}
          </span>
        </div>
      </div>
    </div>

    <div
      data-id="chart"
      class="isolate ml-auto grid grid-flow-row auto-rows-auto gap-y-2 rounded-2xl bg-white p-4 shadow ring-1 ring-neutral-200"
      :style="{ opacity: 0 }"
    >
      <span class="font-medium">
        Events & Catering Revenue
      </span>

      <div
        class="grid auto-cols-fr grid-flow-col items-end gap-x-2"
        data-id="chart-graph"
        :style="{ opacity: 0 }"
      >
        <div
          v-for="(month, monthIndex) in months"
          :key="month"
          class="grid place-content-end gap-y-2 text-center"
          :style="{
            minHeight: `${getHeight(months.length - 1) + 24}px`
          }"
        >
          <span
            data-id="chart-bar"
            class="block h-[--tw-height] w-full rounded-lg bg-orange-500"
            :style="{
              '--tw-height': `${getHeight(monthIndex)}px`,
              '--tw-bg-opacity': (1 / 7 * (monthIndex + 1)).toFixed(2)
            }"
          />

          <span class="min-w-6 text-xs text-neutral-500">
            {{ month }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>
